import { useRef, useEffect } from 'react';
import { chunk } from 'lodash-es';

export const useFocusGroup = (
    className: string,
    { ltr = true }: { ltr?: boolean } = {}
) => {
    if (!className.startsWith('.')) {
        throw new Error(
            `useFocusGroup:\nExpected: a class name selector as a parameter (string that starts with a dot).\nReceived: "${className}"`
        );
    }

    if (className.split(' ').length === 1) {
        // eslint-disable-next-line no-console
        console.warn(
            `useFocusGroup:\nIt's highly recommended to pass a selector that includes the class name of the element that contains all the items you want to group.\nThis is essential in order to avoid querying generic components that are rendered simultaneously on different parts of the screen.` +
                `%c\nBad: ".item"` +
                `%c\nGood: ".container .item"`,
            'color: red',
            'color: lightgreen'
        );
    }

    const lastFocusedItem = useRef<HTMLElement>();

    useEffect(() => {
        function onKeyDown(event: KeyboardEvent) {
            const key = event.key as TKeys;
            const target = event.target as HTMLElement;

            if (!KEYS.includes(key)) {
                return;
            }

            const items = [
                ...document.querySelectorAll(className)
            ] as HTMLElement[];

            if (!items.length) {
                return;
            }

            items.forEach(item => {
                item.querySelectorAll('*').forEach(
                    (descendant: HTMLElement) => {
                        if (
                            item !== lastFocusedItem.current &&
                            descendant.tabIndex > -1
                        ) {
                            descendant.setAttribute(focusAttr, 'true');
                            descendant.tabIndex = -1;
                        }
                    }
                );
            });

            const rows: HTMLElement[][] = (() => {
                const container = items[0].parentElement;
                const { display, gridTemplateColumns } =
                    getComputedStyle(container);

                if (display === 'grid') {
                    const columnCount = gridTemplateColumns.split(' ').length;
                    return chunk(items, columnCount);
                }
                if (items.every(item => item.parentElement === container)) {
                    return items.map(item => [item]);
                }
                return items
                    .filter(
                        item => item === item.parentElement.firstElementChild
                    )
                    .map(
                        item =>
                            [...item.parentElement.children] as HTMLElement[]
                    );
            })();

            if (key === 'Tab') {
                setLastFocusedItem(lastFocusedItem.current);
                return;
            }

            const [currRowIndex, currItemIndex] = (() => {
                let itemIndex: number;

                const rowIndex = rows.findIndex(row => {
                    const currIndex = row.findIndex(
                        item => item === target || item.contains(target)
                    );
                    if (currIndex !== -1) {
                        itemIndex = currIndex;
                        return true;
                    }
                });

                return [rowIndex, itemIndex];
            })();

            if (currItemIndex === undefined) {
                return;
            }

            const nextItem = (() => {
                switch (key) {
                    case 'ArrowUp': {
                        let rowIndex = currRowIndex - 1;

                        while (rowIndex > -rows.length) {
                            const item = rows.at(rowIndex).at(currItemIndex);
                            if (isFocusable(item)) {
                                return item;
                            }
                            rowIndex--;
                        }
                        break;
                    }
                    case 'ArrowDown': {
                        let rowIndex = currRowIndex - rows.length + 1;

                        while (rowIndex < rows.length) {
                            const item = rows.at(rowIndex).at(currItemIndex);
                            if (isFocusable(item)) {
                                return item;
                            }
                            rowIndex++;
                        }
                        break;
                    }
                    case 'ArrowLeft': {
                        if (!ltr) {
                            return;
                        }
                        let rowIndex = currRowIndex;
                        let itemIndex = currItemIndex - 1;

                        while (rowIndex > -rows.length - 1) {
                            while (itemIndex > -1) {
                                const item = rows.at(rowIndex).at(itemIndex);
                                if (isFocusable(item)) {
                                    return item;
                                }
                                itemIndex--;
                            }
                            rowIndex--;
                            itemIndex = rows.at(rowIndex).length - 1;
                        }
                        break;
                    }
                    case 'ArrowRight': {
                        if (!ltr) {
                            return;
                        }
                        let rowIndex = currRowIndex - rows.length;
                        let itemIndex = currItemIndex + 1;

                        while (rowIndex < rows.length) {
                            while (itemIndex < rows.at(rowIndex).length) {
                                const item = rows.at(rowIndex).at(itemIndex);
                                if (isFocusable(item)) {
                                    return item;
                                }
                                itemIndex++;
                            }
                            rowIndex++;
                            itemIndex = 0;
                        }
                        break;
                    }
                }
            })();

            if (isFocusable(nextItem)) {
                event.preventDefault();
                setLastFocusedItem(nextItem);
                nextItem.focus();
            }

            function isFocusable(item: HTMLElement) {
                return (
                    item &&
                    document.body.contains(item) &&
                    item.getAttribute('aria-disabled') !== 'true' &&
                    item.getAttribute('inert') !== 'true' &&
                    !(item as HTMLInputElement).disabled
                );
            }

            function setLastFocusedItem(item: HTMLElement) {
                lastFocusedItem.current = isFocusable(item)
                    ? item
                    : items.find(isFocusable);

                items.forEach(currItem => {
                    const tabIndex =
                        currItem === lastFocusedItem.current ? 0 : -1;
                    currItem.tabIndex = tabIndex;

                    currItem
                        .querySelectorAll('*')
                        .forEach((descendant: HTMLElement) => {
                            if (descendant.getAttribute(focusAttr) === 'true') {
                                descendant.tabIndex = tabIndex;
                            }
                        });
                });
            }
        }

        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [className, ltr]);

    return { lastFocusedItem };
};

const focusAttr = 'data-focusable-descendant';

const KEYS = [
    'Tab',
    'ArrowDown',
    'ArrowUp',
    'ArrowLeft',
    'ArrowRight'
] as const;

type TKeys = (typeof KEYS)[number];
